import { DoubleSide, Group, Shape, Vector2, Vector3 } from 'three'
import LayerKeys from '~/config/layerKeys'
import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { ComfortZone } from '~/store/objects/types'
import { modelToUI, uiToModel } from '../util/units'
import { Html, Line } from '@react-three/drei'
import { useRef, useState } from 'react'
import ObjectClassName from '~/config/objectClassNames'
import { selectObjects } from '~/store/selectedObjects'
import { apply, transformMatrix } from './utils'
import { updateComfortZone } from '~/store/objects'
import { BoxDragControl } from '../util/boxDragControls'
import { cornerPoints } from '../util/boxDragUtil'
import { useModifiers } from '../hooks/modifiers'
import { DragControls } from '../util/DragControls'
import { handleClick } from '~/components/DrawingCanvas/util/eventHandlers'

export function ComfortZones() {
  const isVisible = useAppSelector(it => it.layers.layers[LayerKeys.COMFORT_ZONES].visible)
  const comfortZones = useAppSelector(it => it.objects.present.comfortZones)

  return (
    <group visible={isVisible}>
      {Object.entries(comfortZones).map(([key, value]) => (
        <AComfortZone key={key} comfortZone={value} />
      ))}
    </group>
  )
}

function AComfortZone(props: { comfortZone: ComfortZone }) {
  const isVisible = useAppSelector(it => it.layers.layers[LayerKeys.COMFORT_ZONES].visible)
  const points = props.comfortZone.positions
    .slice(0, props.comfortZone.positions.length - 1)
    .map(pt => new Vector3(modelToUI(pt.x), modelToUI(pt.y), modelToUI(pt.z)))
  const [hovered, setHovered] = useState(false)
  const selected = useAppSelector(it => it.selectedObjects.some(sel => sel.id === props.comfortZone.id))
  const dispatch = useAppDispatch()
  const unlocked = useAppSelector(it => !it.layers.layers.COMFORT_ZONES.locked)

  const [dragIndex, setDragIndex] = useState<number | undefined>()
  const [translateDrag, setTranslateDrag] = useState(false)
  const [deltaAccumulator, setDeltaAccumulator] = useState<[number, number]>([0, 0])

  const groupRef = useRef<Group>(null)
  const currentGesture = useRef<'none' | 'drag' | 'resize'>('none')

  const anyActiveDrag = dragIndex !== undefined || translateDrag
  const height = 48

  const { bounding: boundingBoxPoints, handles: cornerHandles } = cornerPoints(points, height)

  const shiftDown = useModifiers(modifiers => modifiers.shift)

  return (
    <group name={LayerKeys.COMFORT_ZONES}>
      {isVisible && (
        <Html position={new Vector3().copy(points[0])}>
          {!anyActiveDrag && (
            <>
              <div className="text-nowrap bg-neutral-600 rounded text-sm px-1 text-white -translate-y-full pointer-events-none">
                {props.comfortZone.name}
              </div>
            </>
          )}
        </Html>
      )}
      <group position={new Vector3(0, 0, height)}>
        <Line
          points={[...points, points[0]]}
          color={props.comfortZone.color}
          dashed
          lineWidth={2}
          visible={!anyActiveDrag}
        />
      </group>
      <Line
        points={[...points, points[0]]}
        color={props.comfortZone.color}
        dashed
        lineWidth={2}
        visible={!anyActiveDrag}
      />
      <DragControls
        dragDisabled={!selected || !unlocked}
        onDragStart={() => {
          if (currentGesture.current !== 'none') {
            return
          }
          currentGesture.current = 'drag'
          setTranslateDrag(true)
          setDeltaAccumulator([0, 0])
        }}
        onDrag={(_event, delta) => {
          if (currentGesture.current !== 'drag') {
            return
          }
          groupRef.current?.position.set(deltaAccumulator[0], deltaAccumulator[1], 0)
          setDeltaAccumulator([delta.x, delta.y])
        }}
        onDragEnd={() => {
          if (currentGesture.current !== 'drag') {
            return
          }
          currentGesture.current = 'none'
          setTranslateDrag(false)

          const transformedPoints = points.map(it =>
            it.clone().add({ x: deltaAccumulator[0], y: deltaAccumulator[1], z: 0 })
          )
          transformedPoints.push(transformedPoints[0])
          const newPoints = transformedPoints.map(pt => ({
            x: uiToModel(pt.x),
            y: uiToModel(pt.y),
            z: uiToModel(pt.z),
          }))

          dispatch(
            updateComfortZone({
              comfortZone: {
                id: props.comfortZone.id,
                positions: newPoints,
              },
            })
          )
        }}
        onTap={handleClick(() => {
          dispatch(selectObjects({ objects: [{ id: props.comfortZone.id, className: ObjectClassName.COMFORT_ZONE }] }))
        })}
      >
        <mesh onPointerOver={() => setHovered(true)} onPointerOut={() => setHovered(false)} visible={!anyActiveDrag}>
          <extrudeGeometry
            args={[new Shape(points.map(pt => new Vector2().copy(pt))), { depth: height }]}
            attach="geometry"
          />
          <meshStandardMaterial
            color={props.comfortZone.color}
            transparent
            depthWrite={false}
            side={DoubleSide}
            opacity={selected ? (hovered ? 0.4 : 0.3) : hovered ? 0.2 : 0.1}
          />
        </mesh>
      </DragControls>
      <group position={new Vector3(0, 0, height)} visible={selected && !anyActiveDrag}>
        <Line points={boundingBoxPoints} color="black" dashed lineWidth={2} />
      </group>
      <Line points={boundingBoxPoints} color="black" dashed lineWidth={2} visible={selected && !anyActiveDrag} />
      {cornerHandles.map((pt, idx) => {
        return (
          <BoxDragControl
            key={idx.toString()}
            visible={selected && unlocked && !anyActiveDrag}
            position={pt}
            onDragStart={() => {
              if (currentGesture.current !== 'none') {
                return
              }
              currentGesture.current = 'resize'
              setDragIndex(idx)
              setDeltaAccumulator([0, 0])
            }}
            onDrag={(_, delta) => {
              if (currentGesture.current !== 'resize') {
                return
              }
              setDeltaAccumulator([delta.x, delta.y])
            }}
            onDragEnd={() => {
              if (currentGesture.current !== 'resize') {
                return
              }
              currentGesture.current = 'none'
              setDragIndex(undefined)

              const oppositeIndex = (dragIndex! + Math.trunc(cornerHandles.length / 2)) % cornerHandles.length
              const mtx = transformMatrix(
                cornerHandles[oppositeIndex],
                cornerHandles[dragIndex!],
                { x: deltaAccumulator[0], y: deltaAccumulator[1], z: 0 },
                shiftDown
              )
              const transformedPoints = points.map(it => apply(it, mtx))
              transformedPoints.push(transformedPoints[0])

              const newPoints = transformedPoints.map(pt => ({
                x: uiToModel(pt.x),
                y: uiToModel(pt.y),
                z: uiToModel(pt.z),
              }))

              dispatch(
                updateComfortZone({
                  comfortZone: {
                    id: props.comfortZone.id,
                    positions: newPoints,
                  },
                })
              )
            }}
          />
        )
      })}
      {dragIndex !== undefined &&
        (() => {
          const oppositeIndex = (dragIndex + Math.trunc(cornerHandles.length / 2)) % cornerHandles.length
          const mtx = transformMatrix(
            cornerHandles[oppositeIndex],
            cornerHandles[dragIndex],
            { x: deltaAccumulator[0], y: deltaAccumulator[1], z: 0 },
            shiftDown
          )
          const transformedPoints = points.map(it => apply(it, mtx))
          const transformedHandles = cornerHandles.map(it => apply(it, mtx))
          return (
            <>
              {transformedHandles.map((pt, idx) => (
                <BoxDragControl position={pt} key={idx.toString()} />
              ))}
              <mesh>
                <extrudeGeometry
                  args={[new Shape(transformedPoints.map(pt => new Vector2().copy(pt))), { depth: height }]}
                  attach="geometry"
                />
                <meshStandardMaterial
                  color={props.comfortZone.color}
                  transparent
                  depthWrite={false}
                  side={DoubleSide}
                  opacity={0.4}
                />
              </mesh>
              <group position={new Vector3(0, 0, height)}>
                <Line
                  points={[...transformedPoints, transformedPoints[0]]}
                  color={props.comfortZone.color}
                  dashed
                  lineWidth={2}
                />
              </group>
              <Line
                points={[...transformedPoints, transformedPoints[0]]}
                color={props.comfortZone.color}
                dashed
                lineWidth={2}
              />
            </>
          )
        })()}
      {translateDrag && (
        <group ref={groupRef}>
          <mesh>
            <extrudeGeometry
              args={[new Shape(points.map(pt => new Vector2().copy(pt))), { depth: height }]}
              attach="geometry"
            />
            <meshStandardMaterial
              color={props.comfortZone.color}
              transparent
              depthWrite={false}
              side={DoubleSide}
              opacity={0.4}
            />
          </mesh>
          <group position={new Vector3(0, 0, height)}>
            <Line points={[...points, points[0]]} color={props.comfortZone.color} dashed lineWidth={2} />
          </group>
          <Line points={[...points, points[0]]} color={props.comfortZone.color} dashed lineWidth={2} />
        </group>
      )}
    </group>
  )
}
